import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Ban, Plus, X, Globe } from "lucide-react";
import { Policy, usePolicies } from "@/hooks/usePolicies";
import { useState } from "react";

interface PresetBlacklistTabProps {
  preset: Policy;
}

export const PresetBlacklistTab = ({ preset }: PresetBlacklistTabProps) => {
  const [newDomain, setNewDomain] = useState("");
  const { updatePolicy } = usePolicies();

  const handleAddDomain = async () => {
    if (newDomain.trim()) {
      try {
        const currentBlocklist = preset.blocklist || [];
        const updatedBlocklist = [...currentBlocklist, newDomain.trim()];
        await updatePolicy(preset._id, { blocklist: updatedBlocklist });
        setNewDomain("");
      } catch (error) {
        console.error('Failed to add domain to blocklist:', error);
      }
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const currentBlocklist = preset.blocklist || [];
      const updatedBlocklist = currentBlocklist.filter(item => item !== domain);
      await updatePolicy(preset._id, { blocklist: updatedBlocklist });
    } catch (error) {
      console.error('Failed to remove domain from blocklist:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Add Blocked Domain</span>
          </CardTitle>
          <CardDescription>
            Add a new item to the blocklist for this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              placeholder="Enter item (e.g., example.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <Button onClick={handleAddDomain} disabled={!newDomain.trim()}>
              Add Item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blacklisted Domains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Ban className="w-5 h-5 text-red-600" />
            <span>Blocked Items</span>
          </CardTitle>
          <CardDescription>
            Items that are explicitly blocked in this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(preset.blocklist?.length || 0) === 0 ? (
            <div className="text-center py-8">
              <Ban className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No items blocked</h3>
              <p className="text-gray-500 mb-4">
                Add items that should be blocked in this policy.
              </p>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preset.blocklist?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-sm">{item}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Blocked
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveDomain(item)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-900">Blacklist Information</h4>
              <p className="text-sm text-red-700 mt-1">
                Items in the blocklist are always blocked, even if they would normally be allowed by category settings. 
                Use this to block specific problematic content.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};